import { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Wand2, X } from 'lucide-react';
import './Admin.css';

function authFetch(url, token, options = {}) {
  return fetch(url, {
    ...options,
    headers: { 'Authorization': `Bearer ${token}`, ...options.headers },
  });
}

function StatusBadge({ status }) {
  const map = {
    ready: { label: 'Ready', cls: 'badge-success' },
    processing: { label: 'Processing', cls: 'badge-warning' },
    error: { label: 'Error', cls: 'badge-error' },
    pending: { label: 'Pending', cls: 'badge-info' },
  };
  const s = map[status] || { label: status, cls: '' };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

export default function DocumentLibrary({ token }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [generateModal, setGenerateModal] = useState(null);
  const [generateForm, setGenerateForm] = useState({ pathName: '', sessionId: '' });
  const [generateStatus, setGenerateStatus] = useState('');
  const [uploadModal, setUploadModal] = useState(null); // { files }
  const [retainOriginal, setRetainOriginal] = useState(false);
  const [redact, setRedact] = useState(false);
  const [redactionMode, setRedactionMode] = useState('placeholder');
  const fileInputRef = useRef();

  async function loadDocuments() {
    try {
      const res = await authFetch('/api/admin/documents', token);
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadDocuments(); }, [token]);

  function handleFilePick(e) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setRetainOriginal(false);
    setRedact(false);
    setRedactionMode('placeholder');
    setUploadModal({ files });
    fileInputRef.current.value = '';
  }

  async function handleUpload() {
    if (!uploadModal) return;
    setUploading(true);
    setUploadModal(null);
    try {
      const formData = new FormData();
      for (const file of uploadModal.files) {
        formData.append('file', file);
      }
      formData.append('retain_original', String(retainOriginal));
      if (redact) {
        formData.append('redact', 'true');
        formData.append('redaction_mode', redactionMode);
      }
      const res = await fetch('/api/admin/documents/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      await loadDocuments();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(doc) {
    try {
      const res = await authFetch(`/api/admin/documents/${doc.id}`, token, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      setDocuments(d => d.filter(x => x.id !== doc.id));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteConfirm(null);
    }
  }

  function openGenerateModal(doc) {
    setGenerateModal(doc);
    setGenerateForm({ pathName: doc.name || doc.filename || '', sessionId: '' });
    setGenerateStatus('');
  }

  async function handleGeneratePath() {
    if (!generateModal) return;
    setGenerateStatus('generating');
    try {
      const res = await authFetch(`/api/admin/documents/${generateModal.id}/generate-path`, token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pathName: generateForm.pathName, sessionId: generateForm.sessionId }),
      });
      if (!res.ok) throw new Error('Generation failed');
      setGenerateStatus('done');
    } catch (err) {
      setGenerateStatus('error:' + err.message);
    }
  }

  if (loading) return <div className="admin-section"><p style={{ color: 'var(--text-tertiary)' }}>Loading...</p></div>;

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <div>
          <h2 className="admin-section-title">Document Library</h2>
          <p className="admin-section-desc">{documents.length} document{documents.length !== 1 ? 's' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.md,.txt,.docx"
            style={{ display: 'none' }}
            onChange={handleFilePick}
          />
          <button
            className="btn-sm btn-primary"
            onClick={() => fileInputRef.current.click()}
            disabled={uploading}
          >
            <Upload size={14} />
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {documents.length === 0 ? (
        <div className="empty-admin">
          <p>No documents uploaded yet.</p>
          <p>Upload PDF, Markdown, or text files to build your document library.</p>
        </div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Filename</th>
              <th>Chunks</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map(doc => (
              <tr key={doc.id}>
                <td className="doc-filename">
                  {doc.filename || doc.name}
                  {doc.redacted && (
                    <span className="badge badge-info" style={{ marginLeft: '6px', fontSize: '11px' }} title={`Redacted (${doc.redaction_mode || 'placeholder'})`}>PII redacted</span>
                  )}
                </td>
                <td style={{ color: 'var(--text-secondary)' }}>{doc.chunkCount ?? doc.chunk_count ?? '—'}</td>
                <td><StatusBadge status={doc.status || 'ready'} /></td>
                <td>
                  <div className="action-btn-group">
                    <button
                      className="action-btn"
                      title="Generate Learning Path"
                      onClick={() => openGenerateModal(doc)}
                    >
                      <Wand2 size={14} />
                    </button>
                    <button
                      className="action-btn action-btn-danger"
                      title="Delete"
                      onClick={() => setDeleteConfirm(doc)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Upload modal */}
      {uploadModal && (
        <div className="admin-modal-overlay" onClick={() => setUploadModal(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Upload Document{uploadModal.files.length > 1 ? 's' : ''}</h3>
              <button className="modal-close" onClick={() => setUploadModal(null)}><X size={16} /></button>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '16px' }}>
              {Array.from(uploadModal.files).map(f => f.name).join(', ')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ background: 'var(--surface-secondary)', borderRadius: '8px', padding: '12px 14px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={retainOriginal}
                    onChange={e => setRetainOriginal(e.target.checked)}
                    style={{ marginTop: '2px', flexShrink: 0 }}
                  />
                  <span>
                    <span style={{ fontSize: '13px', fontWeight: 500 }}>Retain original file</span>
                    <span style={{ display: 'block', fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '3px' }}>
                      Stores the uploaded file so it can be opened later (e.g. to view the original PDF). Leave unchecked for confidential or sensitive documents — the file is discarded after processing and cannot be recovered.
                    </span>
                  </span>
                </label>
              </div>
              <div style={{ background: 'var(--surface-secondary)', borderRadius: '8px', padding: '12px 14px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={redact}
                    onChange={e => setRedact(e.target.checked)}
                    style={{ marginTop: '2px', flexShrink: 0 }}
                  />
                  <span>
                    <span style={{ fontSize: '13px', fontWeight: 500 }}>Redact PII before indexing</span>
                    <span style={{ display: 'block', fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '3px' }}>
                      Detects and removes personally identifiable information (names, emails, phone numbers, IBANs, etc.) before the content is indexed or seen by any AI model.
                    </span>
                  </span>
                </label>
                {redact && (
                  <div style={{ marginTop: '10px', paddingLeft: '26px' }}>
                    <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Redaction mode</label>
                    <select
                      value={redactionMode}
                      onChange={e => setRedactionMode(e.target.value)}
                      style={{ fontSize: '13px', width: '100%' }}
                    >
                      <option value="placeholder">Placeholder — replace with entity type (e.g. [PERSON])</option>
                      <option value="pseudonymize">Pseudonymize — replace with consistent fictional labels (Person A, Location 1…)</option>
                      <option value="suppress">Suppress — replace all PII with [REDACTED]</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="admin-modal-actions" style={{ marginTop: '16px' }}>
              <button className="btn-sm btn-ghost" onClick={() => setUploadModal(null)}>Cancel</button>
              <button className="btn-sm btn-primary" onClick={handleUpload}>
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="admin-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete Document</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirm.filename || deleteConfirm.name}</strong>? This cannot be undone.</p>
            <div className="admin-modal-actions">
              <button className="btn-sm btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn-sm btn-danger" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Generate path modal */}
      {generateModal && (
        <div className="admin-modal-overlay" onClick={() => setGenerateModal(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Generate Learning Path</h3>
              <button className="modal-close" onClick={() => setGenerateModal(null)}><X size={16} /></button>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '16px' }}>
              AI will generate flashcards from <strong>{generateModal.filename || generateModal.name}</strong> and create a learning path.
            </p>
            <div className="admin-form-row">
              <label>Path Name</label>
              <input
                type="text"
                value={generateForm.pathName}
                onChange={e => setGenerateForm(f => ({ ...f, pathName: e.target.value }))}
                placeholder="Learning path name"
              />
            </div>
            <div className="admin-form-row" style={{ marginTop: '12px' }}>
              <label>Session ID <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(required for AI generation)</span></label>
              <input
                type="text"
                value={generateForm.sessionId}
                onChange={e => setGenerateForm(f => ({ ...f, sessionId: e.target.value }))}
                placeholder="Active session ID"
              />
            </div>
            {generateStatus === 'generating' && (
              <p style={{ color: 'var(--text-tertiary)', fontSize: '13px', marginTop: '12px' }}>Generating path…</p>
            )}
            {generateStatus === 'done' && (
              <p style={{ color: 'var(--success)', fontSize: '13px', marginTop: '12px' }}>
                Path created! Check Learning Paths to view it.
              </p>
            )}
            {generateStatus.startsWith('error:') && (
              <p style={{ color: 'var(--error)', fontSize: '13px', marginTop: '12px' }}>
                {generateStatus.slice(6)}
              </p>
            )}
            <div className="admin-modal-actions" style={{ marginTop: '16px' }}>
              <button className="btn-sm btn-ghost" onClick={() => setGenerateModal(null)}>Cancel</button>
              <button
                className="btn-sm btn-primary"
                onClick={handleGeneratePath}
                disabled={generateStatus === 'generating' || !generateForm.pathName}
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
